import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useInternalChatClient } from "@/components/pages/chats/hooks/useInternalChatClient";
import {
  Channel,
  ChannelList,
  Chat,
  InfiniteScroll,
  MessageInput,
  type MessageInputProps,
  MessageList,
  Thread,
  Window,
  useChannelActionContext,
  useChannelStateContext,
} from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";
import type {
  Channel as StreamChannel,
  ChannelFilters,
  ChannelOptions,
  ChannelSort,
} from "stream-chat";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { InternalCustomListItem } from "@/components/pages/chats/component/chat-custom-components/InternalCustomListItem";
import { InternalChannelHeader } from "@/components/pages/chats/component/chat-custom-components/InternalChannelHeader";
import { CreateGroupChatButton } from "@/components/pages/chats/component/chat-custom-components/CreateGroupChatButton";
import CustomMessageInput from "@/components/pages/chats/component/chat-custom-components/CustomMessageInput";
import { CustomMessageUi } from "@/components/pages/chats/component/chat-custom-components/CustomMessageUi";
import { InternalTeamsBar } from "@/components/pages/chats/component/InternalTeamsBar";
import { InternalChatDetailsPanel } from "@/components/pages/chats/component/InternalChatDetailsPanel";
import {
  addAllMentionToLocalMessage,
  addAllMentionToMessage,
} from "@/components/pages/chats/utils/channel-mentions";
import {
  createMessageSearchFunction,
  MessageSearchResultItem,
} from "@/components/pages/chats/utils/message-search";
import { ArrowLeftToLine } from "lucide-react";
import { motion } from "framer-motion";

type InternalChatFilter = "inbox" | "all";

const InternalCustomMessageInput: React.FC<MessageInputProps> = (props) => (
  <CustomMessageInput
    {...props}
    variant="internal"
    hideResolvePanel
    hideVariableSelectors
    hideAiSettingsPanel
    hideSuggestButton
  />
);

const InternalMessageInput: React.FC = () => {
  const { channel } = useChannelStateContext();
  const { sendMessage } = useChannelActionContext();

  const overrideSubmitHandler = useCallback<
    NonNullable<MessageInputProps["overrideSubmitHandler"]>
  >(
    async ({ localMessage, message, sendOptions }) => {
      await sendMessage({
        localMessage: addAllMentionToLocalMessage(localMessage, channel),
        message: addAllMentionToMessage(message, channel),
        options: sendOptions,
      });
    },
    [channel, sendMessage],
  );

  return (
    <MessageInput
      Input={InternalCustomMessageInput}
      overrideSubmitHandler={overrideSubmitHandler}
      asyncMessagesMultiSendEnabled
      audioRecordingEnabled
    />
  );
};

const InternalChatsPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const activeChannel = searchParams.get("activeChannel");
  const { client, userId, isLoading } = useInternalChatClient();
  const [activeFilter, setActiveFilter] = useState<InternalChatFilter>("inbox");
  const [isInfoOpen, setIsInfoOpen] = useState(true);
  const [unreadChannelCids, setUnreadChannelCids] = useState<
    ReadonlySet<string>
  >(() => new Set());

  const options = useMemo<ChannelOptions>(
    () => ({ presence: true, state: true }),
    [],
  );
  const sort = useMemo<ChannelSort>(
    () =>
      activeFilter === "inbox"
        ? [{ has_unread: -1 }, { last_message_at: -1 }]
        : { last_message_at: -1 },
    [activeFilter],
  );
  const filters = useMemo<ChannelFilters>(
    () => ({ members: { $in: [userId] }, type: "messaging" }),
    [userId],
  );
  const channelSearchProps = useMemo(
    () => ({
      placeholder: t("chats.searchMessages"),
      searchForChannels: false,
      searchForUsers: false,
      searchFunction: createMessageSearchFunction({
        client,
        filters,
        options,
      }),
      SearchResultItem: MessageSearchResultItem,
    }),
    [client, filters, options, t],
  );
  const filterChannels = useCallback(
    (channels: StreamChannel[]) => {
      if (activeFilter === "all") return channels;

      return channels.filter(
        (channel) =>
          unreadChannelCids.has(channel.cid) || channel.countUnread() > 0,
      );
    },
    [activeFilter, unreadChannelCids],
  );
  const handleFilterChange = useCallback((value: string) => {
    if (value === "inbox" || value === "all") {
      setActiveFilter(value);
    }
  }, []);
  const handleCollapseInfo = useCallback(() => setIsInfoOpen(false), []);
  const handleExpandInfo = useCallback(() => setIsInfoOpen(true), []);

  useEffect(() => {
    if (!client) return;

    const refreshUnreadChannels = () => {
      const unreadCids = Object.values(client.activeChannels)
        .filter((channel) => channel.countUnread() > 0)
        .map((channel) => channel.cid);

      setUnreadChannelCids(new Set(unreadCids));
    };
    refreshUnreadChannels();
    const handlers = [
      client.on("message.new", refreshUnreadChannels),
      client.on("notification.message_new", refreshUnreadChannels),
      client.on("notification.mark_read", refreshUnreadChannels),
      client.on("notification.mark_unread", refreshUnreadChannels),
    ];

    return () => {
      handlers.forEach((handler) => handler.unsubscribe());
    };
  }, [client]);

  if (isLoading || !client || !userId) return null;

  return (
    <div className="flex h-full max-h-[calc(100dvh-80px)] min-h-0 flex-row overflow-hidden">
      <Chat client={client}>
        <div className="flex h-full min-h-0 w-full flex-row overflow-hidden">
          <div className="flex h-full min-h-0 w-full max-w-1/3 flex-col">
            <div className="flex h-[62px] shrink-0 items-center gap-2 border-b border-neutral-200 px-4">
              <CreateGroupChatButton />
              <Tabs
                value={activeFilter}
                onValueChange={handleFilterChange}
                className="ml-auto"
              >
                <TabsList className="flex h-8 items-center gap-0.5 rounded-[10px] bg-neutral-100 p-[3px]">
                  <TabsTrigger
                    value="inbox"
                    className="h-6 rounded-lg px-3 text-sm font-medium text-neutral-500 shadow-none data-[state=active]:text-neutral-950 data-[state=active]:shadow-sm"
                  >
                    {t("chats.inbox")}
                  </TabsTrigger>
                  <TabsTrigger
                    value="all"
                    className="h-6 rounded-lg px-3 text-sm font-medium text-neutral-500 shadow-none data-[state=active]:text-neutral-950 data-[state=active]:shadow-sm"
                  >
                    {t("chats.all")}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <InternalTeamsBar
              client={client}
              currentUserId={userId}
              activeChannelId={activeChannel}
            />
            <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pt-2">
              <ChannelList
                key={`internal-list-${activeFilter}`}
                Preview={InternalCustomListItem}
                Paginator={InfiniteScroll}
                sort={sort}
                filters={filters}
                options={options}
                showChannelSearch
                additionalChannelSearchProps={channelSearchProps}
                channelRenderFilterFn={filterChannels}
                customActiveChannel={activeChannel ?? undefined}
              />
            </div>
          </div>
          <div className="flex min-h-0 min-w-0 grow flex-col overflow-hidden">
            <Channel Message={CustomMessageUi}>
              <Window>
                <InternalChannelHeader />
                <MessageList returnAllReadData />
                <InternalMessageInput />
              </Window>
              <motion.div
                initial={false}
                animate={{ width: isInfoOpen ? "300px" : "2.5rem" }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="flex h-full min-h-0 shrink-0 flex-col overflow-hidden"
              >
                {isInfoOpen ? (
                  <InternalChatDetailsPanel
                    className="min-h-0 w-full flex-1"
                    onCollapse={handleCollapseInfo}
                  />
                ) : (
                  <div className="flex h-full w-full min-w-0 flex-col pt-4">
                    <Button
                      type="button"
                      variant="new_ghost"
                      size="icon_sm"
                      className="!bg-transparent text-neutral-600"
                      aria-label={t("chats.expandPanel")}
                      onClick={handleExpandInfo}
                    >
                      <ArrowLeftToLine className="size-5" aria-hidden />
                    </Button>
                  </div>
                )}
              </motion.div>
              <Thread />
            </Channel>
          </div>
        </div>
      </Chat>
    </div>
  );
};

export default InternalChatsPage;
